import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { Exception, GeneralErrors } from '@labradorsports/utils';

export interface MainState {
    offline: boolean;
    focused: boolean;
    suppressPopup: boolean;
    loadingCount: number;
    genericError: Exception;
    genericAlert: string;
    actionLog: string[];
    featureFlags: {
        [k: string]: boolean;
    };
}

const mainDefaultState: MainState = {
    offline: false,
    focused: true,
    suppressPopup: false,
    loadingCount: 0,
    genericError: undefined,
    genericAlert: undefined,
    actionLog: [],
    featureFlags: {},
};

const actionBlacklist = ['main/Loading'];

const { reducer: mainReducer, actions: mainActions } = createSlice({
    name: 'main',
    initialState: mainDefaultState,
    reducers: {
        SuppressPopup: (state, action) => {
            state.suppressPopup = action.payload;
        },

        Loading: (state, action) => {
            state.loadingCount = Math.max(state.loadingCount + (action.payload ? 1 : -1), 0);
        },

        GenericError: (state, action: PayloadAction<Exception | null>) => {
            // Suppress loading errors if the app is not focused
            if (action.payload && action.payload.code === GeneralErrors.LOADING_ERROR.code && !state.focused) {
                return;
            }

            state.genericError = action.payload;
        },

        GenericAlert: (state, action: PayloadAction<string | null>) => {
            state.genericAlert = action.payload;
        },

        UpdateFeatureFlags: (state, action) => {
            if (action.payload) {
                Object.assign(state.featureFlags, action.payload);
            }
        },

        SetOffline: (state, action) => {
            state.offline = action.payload;
        },

        SetFocused: (state, action) => {
            state.focused = action.payload;
        },
    },

    extraReducers: (builder) =>
        builder
            .addMatcher(
                (action): action is any => !actionBlacklist.includes(action.type) && !action.type.startsWith('api/'),
                (state, action) => {
                    state.actionLog = state.actionLog.concat([action.type]).slice(-100);
                }
            )
            .addDefaultCase(() => {
                // Do nothing
            }),
});

export { mainReducer, mainActions, mainDefaultState };
